import { UC_TIERS } from '@syncanix/shared';
import { ScrollableRegion } from '@syncanix/ui';
import { Check, Minus } from 'lucide-react';

import { useT } from '@/core/I18nProvider';
import { formatUsd } from '@/features/pricing-card/billing-period';

import {
  FEATURE_MATRIX_SECTIONS,
  FEATURE_MATRIX_TIERS,
  type FeatureCell,
} from './feature-matrix-data';

import type { PricingTierId } from '@syncanix/shared';
import type { ReactElement } from 'react';

/**
 * PricingFeatureMatrix (ADR-0149) — the full per-tier feature-comparison table
 * on the marketing `/pricing` page. Rows are features grouped into sections
 * (Usage · Product · Trust · Support); columns are the 5 tiers. Data + every
 * numeric cell come from `feature-matrix-data.ts` (SOT-drift-guarded).
 *
 * Accessibility: a semantic `<table>` (screen readers announce row + column
 * context), `<th scope>` on tier columns / feature rows / section groups, an
 * sr-only caption, and availability marks paired with visually-hidden
 * "Included / Not included" text (never a bare icon).
 *
 * Layout: the whole table lives in ONE two-axis scroll container
 * (`overflow-auto` + a capped max-height) so both the header row (`sticky
 * top-0`) and the feature column (`sticky start-0`, logical) pin while the body
 * scrolls — on mobile the columns scroll horizontally, the rows vertically, and
 * both sticky edges hold. Logical properties only (RTL-safe). The container
 * carries `contain:paint` so the wide table's scrollable overflow stays inside
 * the box and never leaks into the document's scroll width — without it Chromium
 * lets the sticky-column scroll container expand `documentElement.scrollWidth`,
 * which made the whole /pricing page scroll horizontally on narrow viewports.
 */

function AvailabilityCell({ value }: { value: boolean }): ReactElement {
  const { t } = useT();
  return (
    <>
      {value ? (
        <Check aria-hidden="true" className="mx-auto h-5 w-5 text-primary-strong" />
      ) : (
        <Minus
          aria-hidden="true"
          className="mx-auto h-5 w-5 text-[color:color-mix(in_srgb,var(--bd-muted)_60%,transparent)]"
        />
      )}
      <span className="sr-only">
        {t(
          value ? 'pricing.featureMatrix.a11y.included' : 'pricing.featureMatrix.a11y.notIncluded',
        )}
      </span>
    </>
  );
}

function ValueCell({
  text,
  textKey,
}: {
  text?: string | undefined;
  textKey?: string | undefined;
}): ReactElement {
  const { t } = useT();
  return <span className="tabular-nums">{textKey !== undefined ? t(textKey) : text}</span>;
}

function Cell({ cell }: { cell: FeatureCell }): ReactElement {
  return cell.kind === 'bool' ? (
    <AvailabilityCell value={cell.value} />
  ) : (
    <ValueCell text={cell.text} textKey={cell.textKey} />
  );
}

function tierPriceLabel(tier: PricingTierId, custom: string): string {
  const price = UC_TIERS[tier].monthlyPriceUsd;
  return price === null ? custom : formatUsd(price);
}

export function PricingFeatureMatrix(): ReactElement {
  const { t } = useT();

  return (
    <section
      aria-labelledby="feature-matrix-heading"
      aria-label={t('pricing.featureMatrix.regionLabel')}
      className="border-b border-[color:var(--bd-line-3)] bg-[color:var(--bd-page)] px-6 py-16 sm:py-20"
    >
      <div className="mx-auto max-w-6xl">
        <h2
          id="feature-matrix-heading"
          className="text-balance text-[clamp(1.75rem,3vw,2.35rem)] font-semibold leading-[1.08] tracking-[-0.04em] text-[color:var(--bd-ink)]"
        >
          {t('pricing.featureMatrix.heading')}
        </h2>
        <p className="mt-3.5 max-w-3xl text-pretty text-[1.05rem] leading-[1.62] text-[color:var(--bd-body)]">
          {t('pricing.featureMatrix.intro')}
        </p>

        <ScrollableRegion
          aria-label={t('pricing.featureMatrix.caption')}
          className="mt-10 overflow-auto rounded-[var(--bd-radius)] border border-[color:var(--bd-line)] [contain:paint]"
          style={{ maxHeight: '38rem' }}
        >
          <table className="w-full border-collapse text-start text-sm">
            <caption className="sr-only">{t('pricing.featureMatrix.caption')}</caption>
            <thead>
              <tr>
                <th
                  scope="col"
                  // The header carries a 2px rule where the body rows carry a
                  // hairline, so the column names read as a header rather than
                  // as the table's first row.
                  className="sticky start-0 top-0 z-30 min-w-52 border-b-2 border-[color:var(--bd-line-3)] bg-[color:var(--bd-page)] px-4 pb-3 pt-4 text-start align-bottom font-mono rtl:font-sans text-[10.5px] uppercase tracking-[0.13em] text-[color:var(--bd-faint)] rtl:normal-case rtl:tracking-normal"
                >
                  {t('pricing.featureMatrix.col.feature')}
                </th>
                {FEATURE_MATRIX_TIERS.map((tier) => (
                  <th
                    key={tier}
                    scope="col"
                    className="sticky top-0 z-20 min-w-28 border-b-2 border-[color:var(--bd-line-3)] bg-[color:var(--bd-page)] px-4 pb-3 pt-4 text-center align-bottom font-semibold text-[color:var(--bd-ink)]"
                  >
                    <div className="text-[1.02rem] tracking-[-0.022em]">
                      {UC_TIERS[tier].displayName}
                    </div>
                    <div className="mt-0.5 text-xs font-normal tabular-nums text-[color:var(--bd-muted)]">
                      {tierPriceLabel(tier, t('pricing.featureMatrix.value.custom'))}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            {FEATURE_MATRIX_SECTIONS.map((section) => (
              <tbody key={section.id} data-section={section.id}>
                <tr>
                  <th
                    scope="colgroup"
                    colSpan={FEATURE_MATRIX_TIERS.length + 1}
                    className="border-b border-[color:var(--bd-line)] bg-[color:var(--bd-sunk)] px-4 py-2 text-start font-mono rtl:font-sans text-[10.5px] uppercase tracking-[0.13em] rtl:normal-case rtl:tracking-normal text-[color:var(--bd-muted)]"
                  >
                    {t(section.titleKey)}
                  </th>
                </tr>
                {section.rows.map((row) => (
                  // A five-column table is read ACROSS, so the row needs a
                  // highlight to track by. The sticky feature column paints its
                  // own opaque ground (it has to — the body scrolls under it),
                  // so the tint cannot cascade and is applied to both through
                  // `group-hover`. `motion-safe` only: this is a pointer
                  // affordance, not information.
                  <tr
                    key={row.id}
                    data-row={row.id}
                    className="group border-b border-[color:var(--bd-line)] last:border-b-0"
                  >
                    <th
                      scope="row"
                      className="sticky start-0 z-10 border-b border-[color:var(--bd-line)] bg-[color:var(--bd-page)] px-4 py-3 text-start align-top font-medium text-[color:var(--bd-ink)] transition-colors group-hover:bg-[color:var(--bd-sunk)]"
                    >
                      <span>{t(row.labelKey)}</span>
                      {row.hintKey !== undefined ? (
                        <span className="mt-0.5 block text-xs font-normal text-[color:var(--bd-muted)]">
                          {t(row.hintKey)}
                        </span>
                      ) : null}
                    </th>
                    {FEATURE_MATRIX_TIERS.map((tier) => (
                      <td
                        key={tier}
                        data-cell={`${row.id}:${tier}`}
                        className="border-b border-[color:var(--bd-line)] px-4 py-3 text-center align-top text-[color:var(--bd-ink)] transition-colors group-hover:bg-[color:var(--bd-sunk)]"
                      >
                        <Cell cell={row.cells[tier]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </ScrollableRegion>

        <p className="mt-6 max-w-3xl text-xs text-[color:var(--bd-muted)]">
          {t('pricing.featureMatrix.footnote')}
        </p>
      </div>
    </section>
  );
}
